import { KFTextInput } from "@kf/ic2fx";
import { List, ListItem, ListItemButton, ListItemText } from "@mui/material";
import Popover from "@mui/material/Popover";
import _, { find } from "lodash";
import * as React from "react";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import SearchIcon from "../../../assets/icons/Search.svg";
import pencilIcon from "../../../assets/icons/sjpr_pencil.svg";
import { RootState } from "../../../store/reducers";
import { setList } from "../../../store/slices/configSlice";
import {
  CountryListThunk,
  getCountryOptionKey,
  setSelectedCountry
} from "../../../store/slices/countryDetailsSlice";
import {
  getCurrencyListThunk,
  setSelectedCurrency
} from "../../../store/slices/currencyDetailsSlice";
import { updateJobPricingPayloads } from "../../../store/slices/jobPricingSearchSlice";
import { resetPrepopulatedSearches } from "../../../store/slices/jobPricingSlice";
import Loader from "../../loader";
import styles from "./index.module.scss";
import "./index.scss";

export interface IsimpleDialogProps {
  open?: boolean | undefined;
  selectedValue: Icountry;
  onClose: (value: Icountry) => void;
  searchText?: string;
}

export interface Icountry {
  key: string;
  label: string;
  countryId?: string;
  productBrandId?: string;
  uniqueId?: string;
}

const CountryDialog = (props: IsimpleDialogProps) => {
  const { onClose, selectedValue, open } = props;
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { countryList, isCountryListLoading, selectedCountry } = useSelector(
    (state: RootState) => state.country
  );

  const [countryListState, setCountryListState] = useState<Icountry[]>();
  const [allCountries, setAllCountries] = useState<Icountry[]>();

  const handleListItemClick = (value: any) => {
    onClose(value);
    dispatch(setSelectedCountry(value));
    dispatch(setList([]));
    if (!selectedCountry?.key || selectedCountry?.key !== value?.key) {
      dispatch(resetPrepopulatedSearches());
    }
    updatedCountryList(value);
  };

  const updatedCountryList = (value: any) => {
    let countryListCopy = [...countryList];
    let FilteredCountryList = countryListCopy.filter(
      (country: Icountry) =>
        getCountryOptionKey(country) !== getCountryOptionKey(value)
    );
    setCountryListState(FilteredCountryList);
    setAllCountries(FilteredCountryList);
  };

  useEffect(() => {
    countryList?.length === 0 &&
      isCountryListLoading === false &&
      dispatch(CountryListThunk(""));
    updatedCountryList(selectedValue || "");
  }, [countryList]);

  useEffect(() => {
    if (props.searchText) {
      let countryListCopy = [...countryList];
      const search = props.searchText.toLowerCase();
      let FilteredCountryList = countryListCopy.filter((country: Icountry) =>
        country.label?.toLowerCase().includes(search)
      );
      setCountryListState(FilteredCountryList);
      setAllCountries(FilteredCountryList);
    } else {
      let countryListCopy = [...countryList];
      setAllCountries(countryListCopy);
    }
  }, [props.searchText]);

  return (
    <>
      {allCountries && allCountries?.length > 0 ? (
        <List className={styles.countryListModel}>
          {allCountries?.map((country: Icountry) => (
            <ListItem
              disableGutters
              key={getCountryOptionKey(country)}
              className={styles.countryListItem}
            >
              <ListItemButton onClick={() => handleListItemClick(country)}>
                <ListItemText primary={country?.label} />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      ) : (
        <div className={styles.notFoundBlock}>{t("pa.noDataFound")}</div>
      )}
    </>
  );
};

export default function ChooseCountry() {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [selectedValue, setSelectedValue] = React.useState<Icountry>();
  const { selectedCountry, countryList } = useSelector(
    (state: any) => state.country
  );
  const { selectedClient } = useSelector((state: any) => state.clients);
  const { selectedCurrency, currencyList } = useSelector(
    (state: RootState) => state.currency
  );
  const [anchorEl, setAnchorEl] = React.useState<HTMLImageElement | null>(null);
  const [keyword, setKeyword] = useState("");
  const handleClickOpen = (event: React.MouseEvent<HTMLImageElement>) => {
    setKeyword("");
    setAnchorEl(event.currentTarget);
    return;
  };

  const open = Boolean(anchorEl);
  const id = open ? "simple-popover" : undefined;

  const handleClose = (value: Icountry) => {
    setAnchorEl(null);
    setSelectedValue(value);
    dispatch(setSelectedCountry(value));
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
  };

  useEffect(() => {
    if (!currencyList || currencyList.length === 0) {
      dispatch(getCurrencyListThunk({}));
    }
  }, []);

  React.useEffect(() => {
    setSelectedValue(selectedCountry);
    dispatch(
      updateJobPricingPayloads({ countryCode: selectedCountry?.key || "" })
    );
    if (selectedCountry?.key && currencyList && currencyList.length > 0) {
      dispatch(
        setSelectedCurrency(
          (
            find(currencyList, ["countryCode", selectedCountry?.key]) as
              | { isoCurrencyCode?: string }
              | undefined
          )?.isoCurrencyCode || ""
        )
      );
    }
  }, [selectedCountry, currencyList]);

  const initSearch = (_keyword: string) => {
    setKeyword(_keyword);
  };

  const searchDebounce = useCallback(_.debounce(initSearch, 200), []);

  useEffect(() => {
    if (countryList?.length === 0) {
      dispatch(CountryListThunk(selectedClient?.id || ""));
    }
  }, [countryList]);

  return (
    <div className={styles.chooseCountry}>
      <span className={styles.country}>{t("pa.sjpr.countryText")} :</span>
      <span className={styles.selectedCountry} onClick={handleClickOpen}>
        <span>
          {selectedValue?.label ||
            selectedCountry?.label ||
            t("pa.sjpr.selectMarket")}{" "}
          {selectedCountry?.label
            ? selectedCurrency
              ? `(${selectedCurrency})`
              : ""
            : ""}
        </span>
        {countryList?.length > 0 ? (
          <img
            alt="Edit Client"
            className={styles.pencilIcon}
            src={pencilIcon}
          ></img>
        ) : (
          <Loader />
        )}
      </span>
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handlePopoverClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left"
        }}
      >
        <div className={styles.selectedCountryCountaier}>
          <KFTextInput
            variant="standard"
            label=""
            autoComplete="off"
            autoFocus
            placeholder={t("pa.searchCountryPlaceholder")}
            className={styles.multiSelectAutoComplete}
            onChange={(event) => {
              searchDebounce(event.target.value);
            }}
            InputProps={{
              startAdornment: (
                <img
                  src={SearchIcon}
                  alt="search icon"
                  style={{
                    padding: "10px 10px 10px 0px"
                  }}
                />
              )
            }}
          />
          <CountryDialog
            selectedValue={selectedCountry || selectedValue}
            onClose={handleClose}
            open={open}
            searchText={keyword}
          />
        </div>
      </Popover>
    </div>
  );
}
